import { clear, el } from "@bn-games/ui-shell";

export interface AccountHistorySectionStrings {
  readonly title: string;
  readonly loading: string;
  readonly empty: string;
}

export interface MountAccountHistorySectionOptions<TRecord, TStats = never> {
  readonly body: HTMLElement;
  readonly strings: AccountHistorySectionStrings;
  readonly isLoggedIn: () => boolean;
  readonly fetchRecords: () => Promise<readonly TRecord[]>;
  // Guards the async callback against mutating DOM after the screen
  // unmounted — callers pass a closure reading their own `frame` variable.
  readonly isStillMounted: () => boolean;
  readonly renderRow: (record: TRecord) => HTMLElement;
  // Optional one-line "W–L–D (N played)" header above the list. Kept
  // generic and formatting-agnostic — this package has no dependency on
  // @bn-games/game-history-client, so the caller fetches its own stats
  // shape and renders it to a string with its own i18n strings.
  readonly fetchStats?: () => Promise<TStats | null>;
  readonly renderStats?: (stats: TStats) => string;
}

/**
 * Appends a login-gated "Your account" section to `body`: nothing when
 * signed out, else a loading placeholder that resolves in place to either
 * the fetched rows or an empty-state message, optionally preceded by a
 * one-line stats summary when `fetchStats` + `renderStats` are both given.
 * Reuses the same `.history-list`/`.history-empty`/`.history-row__sub`
 * classes the local list already uses, so the section reads as part of the
 * same screen without new CSS.
 *
 * Shared by every game with server-synced online history (F2) — this was
 * near-identical boilerplate independently written into checkers',
 * backgammon's, and domino's `HistoryListScreen.ts` before being lifted
 * here (as `mountOnlineHistorySection`, widened in M7c into the account
 * section with stats).
 */
export function mountAccountHistorySection<TRecord, TStats = never>(
  options: MountAccountHistorySectionOptions<TRecord, TStats>,
): void {
  const { body, strings, isLoggedIn, fetchRecords, isStillMounted, renderRow, fetchStats, renderStats } = options;
  if (!isLoggedIn()) return;

  body.appendChild(el("div", { class: "history-row__title", text: strings.title }));

  if (fetchStats && renderStats) {
    const statsLine = el("div", { class: "history-row__sub" });
    body.appendChild(statsLine);
    void fetchStats().then((stats) => {
      if (!isStillMounted() || stats === null) return;
      statsLine.textContent = renderStats(stats);
    });
  }

  const onlineList = el("div", { class: "history-list" });
  onlineList.appendChild(el("div", { class: "history-empty", text: strings.loading }));
  body.appendChild(onlineList);

  void fetchRecords().then((records) => {
    if (!isStillMounted()) return;
    clear(onlineList);
    if (records.length === 0) {
      onlineList.appendChild(el("div", { class: "history-empty", text: strings.empty }));
      return;
    }
    for (const record of records) {
      onlineList.appendChild(renderRow(record));
    }
  });
}
